import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { selectSelectedCompanyDocument, selectSelectedCompanyOrgId, useAppSelector } from '@ibk/shared-config';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { ProductFamily } from '@ibk/shared-types';
import { Loading } from '@ibk/shared-ui';
import { WithoutContractGuard } from '../../components';
import { WizardStepper } from '../../components/WizardStepper';
import { useCompanyAddress, useEmployeesData } from '../../hooks';
import { useVoucherPartner } from '../../hooks/useVoucherPartner';
import { buildEmissorContractAddress } from '../../services/emissorBenefitsApi';
import { beneficiosTranslations } from '../../translations';
import { HiringStep, SidebarStatus, SidebarStep } from '../../types/hiring';
import { formatLocationAddress } from '../../utils/addressFormatter';
import { HiringAddress } from './components/HiringAddress';
import { HiringEmployees } from './components/HiringEmployees';
import { HiringProductFamily } from './components/HiringProductFamily';
import { HiringTerms } from './components/HiringTerms';
import styles from './HiringPage.module.css';
import { useHiring } from './hooks';
function HiringPageContent() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isPatEnabled, isEmissorFlow, isLoadingFeatureFlags, isLoadingContractData, shouldShowFamilyOption, submitContract, isSubmitting, } = useHiring();
    const usePatMultiFamilyFlow = isPatEnabled && !isEmissorFlow;
    const stepInitializedRef = useRef(false);
    const [currentStep, setCurrentStep] = useState(HiringStep.ADDRESS);
    const [contractData, setContractData] = useState({});
    useLayoutEffect(() => {
        if (isLoadingFeatureFlags || isLoadingContractData || stepInitializedRef.current) {
            return;
        }
        stepInitializedRef.current = true;
        const initialStep = !isEmissorFlow && isPatEnabled ? HiringStep.PRODUCT_FAMILY : HiringStep.ADDRESS;
        setCurrentStep(initialStep);
    }, [isLoadingFeatureFlags, isLoadingContractData, isEmissorFlow, isPatEnabled]);
    const organizationId = useAppSelector(selectSelectedCompanyOrgId);
    const companyDocument = useAppSelector(selectSelectedCompanyDocument);
    const { partner, isLoading: isLoadingPartner, error: partnerError } = useVoucherPartner();
    const { currentLocation } = useCompanyAddress();
    const { employeesData } = useEmployeesData();
    const handleProductFamilyComplete = useCallback((productFamilies) => {
        setContractData(prev => ({
            ...prev,
            productFamilies,
        }));
        setCurrentStep(HiringStep.ADDRESS);
    }, []);
    const handleAddressComplete = useCallback((locationId) => {
        if (!partner?.id) {
            console.error(t('contract.error.partner-not-found'));
            return;
        }
        setContractData(prev => ({
            ...prev,
            voucherPartnerId: partner.id,
            locationId,
        }));
        setCurrentStep(HiringStep.EMPLOYEES);
    }, [partner, t]);
    const handleEmployeesComplete = useCallback(() => {
        if (usePatMultiFamilyFlow && contractData.productFamilies && contractData.productFamilies.length > 0) {
            const sortedFamilies = [...contractData.productFamilies].sort((a, b) => {
                const order = [ProductFamily.AUXILIO, ProductFamily.PAT];
                return order.indexOf(a) - order.indexOf(b);
            });
            setContractData(prev => ({
                ...prev,
                currentTermsFamilyIndex: 0,
            }));
            const firstFamily = sortedFamilies[0];
            const nextStep = firstFamily === ProductFamily.AUXILIO ? HiringStep.TERMS_AUXILIO : HiringStep.TERMS_PAT;
            setCurrentStep(nextStep);
        }
        else {
            setCurrentStep(HiringStep.TERMS);
        }
    }, [usePatMultiFamilyFlow, contractData.productFamilies]);
    const handleBackToAddress = useCallback(() => {
        setContractData(prev => ({
            ...prev,
            currentTermsFamilyIndex: undefined,
        }));
        setCurrentStep(HiringStep.ADDRESS);
    }, []);
    const handleBackToEmployees = useCallback(() => {
        setContractData(prev => ({
            ...prev,
            currentTermsFamilyIndex: undefined,
        }));
        setCurrentStep(HiringStep.EMPLOYEES);
    }, []);
    const handleBackToPreviousTerms = useCallback(() => {
        if (usePatMultiFamilyFlow && contractData.productFamilies && contractData.productFamilies.length > 0) {
            const sortedFamilies = [...contractData.productFamilies].sort((a, b) => {
                const order = [ProductFamily.AUXILIO, ProductFamily.PAT];
                return order.indexOf(a) - order.indexOf(b);
            });
            const currentIndex = contractData.currentTermsFamilyIndex ?? 0;
            if (currentIndex > 0) {
                const previousIndex = currentIndex - 1;
                const previousFamily = sortedFamilies[previousIndex];
                setContractData(prev => ({
                    ...prev,
                    currentTermsFamilyIndex: previousIndex,
                }));
                const previousStep = previousFamily === ProductFamily.AUXILIO ? HiringStep.TERMS_AUXILIO : HiringStep.TERMS_PAT;
                setCurrentStep(previousStep);
            }
            else {
                handleBackToEmployees();
            }
        }
        else {
            handleBackToEmployees();
        }
    }, [usePatMultiFamilyFlow, contractData, handleBackToEmployees]);
    const handleBackToProductFamily = useCallback(() => {
        setContractData(prev => ({
            ...prev,
            currentTermsFamilyIndex: undefined,
        }));
        setCurrentStep(HiringStep.PRODUCT_FAMILY);
    }, []);
    const sortProductFamilies = useCallback((families) => {
        return [...families].sort((a, b) => {
            const order = [ProductFamily.AUXILIO, ProductFamily.PAT];
            return order.indexOf(a) - order.indexOf(b);
        });
    }, []);
    const isTermsStep = useCallback((step) => {
        return step === HiringStep.TERMS || step === HiringStep.TERMS_AUXILIO || step === HiringStep.TERMS_PAT;
    }, []);
    const getProductFamilyLabel = useCallback((family) => {
        return family === ProductFamily.AUXILIO
            ? t('hiring.product-family.short.auxilio')
            : t('hiring.product-family.short.pat');
    }, [t]);
    const getFamilyTermsStatus = useCallback((family, index, currentStep, currentIndex, isLoading) => {
        const isCurrentFamilyTerms = (family === ProductFamily.AUXILIO && currentStep === HiringStep.TERMS_AUXILIO) ||
            (family === ProductFamily.PAT && currentStep === HiringStep.TERMS_PAT);
        if (isCurrentFamilyTerms) {
            return isLoading ? SidebarStatus.COMPLETE : SidebarStatus.ACTIVE;
        }
        if (currentIndex !== undefined && index < currentIndex) {
            return SidebarStatus.COMPLETE;
        }
        return SidebarStatus.INACTIVE;
    }, []);
    const buildProductFamilyStep = useCallback(() => {
        return {
            id: SidebarStep.PRODUCT_FAMILY,
            label: t('hiring.step.product-family'),
            status: currentStep === HiringStep.PRODUCT_FAMILY ? SidebarStatus.ACTIVE : SidebarStatus.COMPLETE,
            editable: currentStep !== HiringStep.PRODUCT_FAMILY,
            onEdit: currentStep !== HiringStep.PRODUCT_FAMILY ? handleBackToProductFamily : undefined,
        };
    }, [currentStep, t, handleBackToProductFamily]);
    const buildAddressStep = useCallback(() => {
        const isActive = currentStep === HiringStep.ADDRESS;
        const isInactive = currentStep === HiringStep.PRODUCT_FAMILY;
        const isEditable = currentStep !== HiringStep.ADDRESS && currentStep !== HiringStep.PRODUCT_FAMILY;
        let status;
        if (isActive) {
            status = SidebarStatus.ACTIVE;
        }
        else if (isInactive) {
            status = SidebarStatus.INACTIVE;
        }
        else {
            status = SidebarStatus.COMPLETE;
        }
        return {
            id: SidebarStep.ADDRESS,
            label: t('hiring.step.address'),
            status,
            editable: isEditable,
            onEdit: isEditable ? handleBackToAddress : undefined,
        };
    }, [currentStep, t, handleBackToAddress]);
    const buildEmployeesStep = useCallback((isAnyTermsStep) => {
        const isActive = currentStep === HiringStep.EMPLOYEES;
        let status;
        if (isActive) {
            status = SidebarStatus.ACTIVE;
        }
        else if (isAnyTermsStep) {
            status = SidebarStatus.COMPLETE;
        }
        else {
            status = SidebarStatus.INACTIVE;
        }
        return {
            id: SidebarStep.EMPLOYEES,
            label: t('hiring.step.employees'),
            status,
            editable: isAnyTermsStep,
            onEdit: isAnyTermsStep ? handleBackToEmployees : undefined,
        };
    }, [currentStep, t, handleBackToEmployees]);
    const buildTermsStepsForPat = useCallback((sortedFamilies, isAnyTermsStep, isLoading) => {
        const termsSteps = [];
        const currentIndex = contractData.currentTermsFamilyIndex;
        if (sortedFamilies.length > 0) {
            sortedFamilies.forEach((family, index) => {
                const stepNumber = SidebarStep.EMPLOYEES + 1 + index;
                const status = getFamilyTermsStatus(family, index, currentStep, currentIndex, isLoading);
                const familyLabel = getProductFamilyLabel(family);
                termsSteps.push({
                    id: stepNumber,
                    label: `${t('hiring.step.contract')} - ${familyLabel}`,
                    status,
                    editable: false,
                });
            });
        }
        else {
            let status;
            if (isAnyTermsStep) {
                status = isLoading ? SidebarStatus.COMPLETE : SidebarStatus.ACTIVE;
            }
            else {
                status = SidebarStatus.INACTIVE;
            }
            termsSteps.push({
                id: SidebarStep.CONTRACT,
                label: t('hiring.step.contract'),
                status,
                editable: false,
                onEdit: undefined,
            });
        }
        return termsSteps;
    }, [contractData.currentTermsFamilyIndex, currentStep, t, getFamilyTermsStatus, getProductFamilyLabel]);
    const buildRequestStep = useCallback((stepNumber, isLoading, labelKey) => {
        return {
            id: stepNumber,
            label: t(labelKey),
            status: isLoading ? SidebarStatus.ACTIVE : SidebarStatus.INACTIVE,
        };
    }, [t]);
    const buildDefaultSteps = useCallback((isLoading) => {
        const isAnyTermsStep = isTermsStep(currentStep);
        const isEmployeesActive = currentStep === HiringStep.EMPLOYEES;
        const isContractActive = isAnyTermsStep && !isLoading;
        const isContractLoading = isAnyTermsStep && isLoading;
        return [
            {
                id: SidebarStep.ADDRESS,
                label: t('hiring.step.address'),
                status: currentStep === HiringStep.ADDRESS ? SidebarStatus.ACTIVE : SidebarStatus.COMPLETE,
                editable: currentStep !== HiringStep.ADDRESS,
                onEdit: currentStep !== HiringStep.ADDRESS ? handleBackToAddress : undefined,
            },
            {
                id: SidebarStep.EMPLOYEES,
                label: t('hiring.step.employees'),
                status: (() => {
                    if (isEmployeesActive) {
                        return SidebarStatus.ACTIVE;
                    }
                    if (isAnyTermsStep) {
                        return SidebarStatus.COMPLETE;
                    }
                    return SidebarStatus.INACTIVE;
                })(),
                editable: isAnyTermsStep,
                onEdit: isAnyTermsStep ? handleBackToEmployees : undefined,
            },
            {
                id: SidebarStep.CONTRACT,
                label: t('hiring.step.contract'),
                status: (() => {
                    if (isContractActive) {
                        return SidebarStatus.ACTIVE;
                    }
                    if (isContractLoading) {
                        return SidebarStatus.COMPLETE;
                    }
                    return SidebarStatus.INACTIVE;
                })(),
                editable: isLoading,
                onEdit: isLoading ? handleBackToEmployees : undefined,
            },
            {
                id: SidebarStep.REQUEST,
                label: t('hiring.step.request'),
                status: isLoading ? SidebarStatus.ACTIVE : SidebarStatus.INACTIVE,
            },
        ];
    }, [currentStep, t, isTermsStep, handleBackToAddress, handleBackToEmployees]);
    const buildPatEnabledSteps = useCallback((isLoading) => {
        const productFamilies = contractData.productFamilies || [];
        const sortedFamilies = productFamilies.length > 0 ? sortProductFamilies(productFamilies) : [];
        const isAnyTermsStep = isTermsStep(currentStep);
        let lastContractStepNumber;
        if (sortedFamilies.length > 0) {
            lastContractStepNumber = (SidebarStep.EMPLOYEES + sortedFamilies.length);
        }
        else {
            lastContractStepNumber = SidebarStep.CONTRACT;
        }
        const steps = [
            buildProductFamilyStep(),
            buildAddressStep(),
            buildEmployeesStep(isAnyTermsStep),
        ];
        const termsSteps = buildTermsStepsForPat(sortedFamilies, isAnyTermsStep, isLoading);
        steps.push(...termsSteps);
        const requestStep = buildRequestStep((lastContractStepNumber + 1), isLoading, 'hiring.step.complete-request');
        steps.push(requestStep);
        return steps;
    }, [
        contractData.productFamilies,
        currentStep,
        sortProductFamilies,
        isTermsStep,
        buildProductFamilyStep,
        buildAddressStep,
        buildEmployeesStep,
        buildTermsStepsForPat,
        buildRequestStep,
    ]);
    const getSidebarSteps = useCallback((isLoading = false) => {
        return usePatMultiFamilyFlow ? buildPatEnabledSteps(isLoading) : buildDefaultSteps(isLoading);
    }, [usePatMultiFamilyFlow, buildPatEnabledSteps, buildDefaultSteps]);
    const handleTermsComplete = useCallback(() => {
        if (usePatMultiFamilyFlow && contractData.productFamilies && contractData.productFamilies.length > 0) {
            const sortedFamilies = [...contractData.productFamilies].sort((a, b) => {
                const order = [ProductFamily.AUXILIO, ProductFamily.PAT];
                return order.indexOf(a) - order.indexOf(b);
            });
            const currentIndex = contractData.currentTermsFamilyIndex ?? 0;
            const nextIndex = currentIndex + 1;
            if (nextIndex < sortedFamilies.length) {
                const nextFamily = sortedFamilies[nextIndex];
                setContractData(prev => ({
                    ...prev,
                    currentTermsFamilyIndex: nextIndex,
                }));
                const nextStep = nextFamily === ProductFamily.AUXILIO ? HiringStep.TERMS_AUXILIO : HiringStep.TERMS_PAT;
                setCurrentStep(nextStep);
            }
            else {
                navigate('/beneficios');
            }
        }
        else {
            navigate('/beneficios');
        }
    }, [usePatMultiFamilyFlow, contractData, navigate]);
    if (isLoadingFeatureFlags || isLoadingContractData) {
        return _jsx(Loading, { fullScreen: true, message: t('benefits.loading') });
    }
    if (partnerError) {
        return (_jsx("div", { className: styles.errorMessage, "data-testid": 'textHiringPageErrorMessage', children: partnerError }));
    }
    const emissorContractAddress = currentLocation ? buildEmissorContractAddress(currentLocation) : undefined;
    const termsContractData = {
        ...contractData,
        document: companyDocument ?? undefined,
        address: emissorContractAddress,
    };
    const wizardSteps = [
        { title: t('hiring.wizard.company-data') },
        { title: t('hiring.wizard.employees') },
        { title: t('hiring.wizard.contract') },
    ];
    const resolveActiveWizardStep = (step) => {
        if (step === HiringStep.EMPLOYEES) {
            return 1;
        }
        return isTermsStep(step) ? 2 : 0;
    };
    const headerStepper = isEmissorFlow ? (_jsx(WizardStepper, { steps: wizardSteps, activeStep: resolveActiveWizardStep(currentStep) })) : null;
    const renderCurrentStep = () => {
        const sidebarSteps = getSidebarSteps();
        const address = currentLocation ? formatLocationAddress(currentLocation) : undefined;
        const employeeCount = employeesData?.employeeCount;
        const sortedProductFamilies = contractData.productFamilies
            ? [...contractData.productFamilies].sort((a, b) => {
                const order = [ProductFamily.AUXILIO, ProductFamily.PAT];
                return order.indexOf(a) - order.indexOf(b);
            })
            : [];
        if (currentStep === HiringStep.PRODUCT_FAMILY) {
            return (_jsx(HiringProductFamily, { onComplete: handleProductFamilyComplete, productFamilies: contractData.productFamilies || [], isLoadingPartner: isLoadingPartner, sidebarSteps: sidebarSteps, productFamiliesForSidebar: sortedProductFamilies, stepNumber: SidebarStep.PRODUCT_FAMILY, shouldShowFamilyOption: shouldShowFamilyOption, "data-testid": 'viewHiringPageProductFamilyStep' }));
        }
        if (currentStep === HiringStep.ADDRESS) {
            const addressStepNumber = usePatMultiFamilyFlow ? SidebarStep.ADDRESS : undefined;
            const onBack = usePatMultiFamilyFlow ? handleBackToProductFamily : undefined;
            return (_jsx(HiringAddress, { onComplete: handleAddressComplete, onBack: onBack, isLoadingPartner: isLoadingPartner, isPatEnabled: usePatMultiFamilyFlow, sidebarSteps: sidebarSteps, address: address, productFamilies: sortedProductFamilies, stepNumber: addressStepNumber, headerStepper: headerStepper, isEmissorFlow: isEmissorFlow, "data-testid": 'viewHiringPageAddressStep' }));
        }
        if (currentStep === HiringStep.EMPLOYEES) {
            const stepNumber = usePatMultiFamilyFlow ? SidebarStep.EMPLOYEES : undefined;
            return (_jsx(HiringEmployees, { onComplete: handleEmployeesComplete, onBack: handleBackToAddress, sidebarSteps: sidebarSteps, address: address, employeeCount: employeeCount, productFamilies: sortedProductFamilies, stepNumber: stepNumber, headerStepper: headerStepper, "data-testid": 'viewHiringPageEmployeesStep' }));
        }
        if (usePatMultiFamilyFlow && (currentStep === HiringStep.TERMS_AUXILIO || currentStep === HiringStep.TERMS_PAT)) {
            const currentFamily = currentStep === HiringStep.TERMS_AUXILIO ? ProductFamily.AUXILIO : ProductFamily.PAT;
            const sortedFamilies = [...sortedProductFamilies].sort((a, b) => {
                const order = [ProductFamily.AUXILIO, ProductFamily.PAT];
                return order.indexOf(a) - order.indexOf(b);
            });
            const currentIndex = contractData.currentTermsFamilyIndex ?? 0;
            const isLastTermsStep = currentIndex === sortedFamilies.length - 1;
            const stepNumber = SidebarStep.EMPLOYEES + 1 + currentIndex;
            return (_jsx(HiringTerms, { onBack: handleBackToPreviousTerms, onEditEmployees: handleBackToEmployees, onComplete: submitContract, onNext: handleTermsComplete, contractData: termsContractData, organizationId: organizationId, sidebarSteps: sidebarSteps, address: address, employeeCount: employeeCount, getSidebarSteps: getSidebarSteps, currentProductFamily: currentFamily, productFamilies: sortedProductFamilies, isLastTermsStep: isLastTermsStep, stepNumber: stepNumber, isPatEnabled: isPatEnabled, isEmissorFlow: isEmissorFlow, isSubmitting: isSubmitting, "data-testid": 'viewHiringPageTermsStep' }));
        }
        return (_jsx(HiringTerms, { onBack: handleBackToEmployees, onEditEmployees: handleBackToEmployees, onComplete: submitContract, contractData: termsContractData, organizationId: organizationId, sidebarSteps: sidebarSteps, address: address, employeeCount: employeeCount, getSidebarSteps: getSidebarSteps, isPatEnabled: isPatEnabled, isEmissorFlow: isEmissorFlow, isSubmitting: isSubmitting, headerStepper: headerStepper, "data-testid": 'viewHiringPageTermsStep' }));
    };
    const content = renderCurrentStep();
    return (_jsx("div", { className: styles.hiringPageContainer, "data-testid": 'viewHiringPageContainer', children: content }));
}
export function HiringPage() {
    return (_jsx(TranslationProvider, { translations: beneficiosTranslations, children: _jsx(WithoutContractGuard, { children: _jsx(HiringPageContent, {}) }) }));
}
